import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard } from '../../../components';
import useViewedListings from '../../../util/useViewedListings';
import useCardDecodedSizes from '../../../hooks/useCardDecodedSizes';

import css from './SearchResultsPanel.module.css';

const ChevronDown = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#b07a2a"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {Array<propTypes.listing>} props.listings
 * @param {propTypes.pagination} props.pagination
 * @param {Object} props.search
 * @param {Function} props.setActiveListing
 * @param {boolean} [props.isMapVariant]
 * @param {Function} [props.onLoadMore]
 * @param {boolean} [props.searchInProgress]
 * @param {Function} [props.onListingClick] called with a listing uuid on result click (Story 8.2 click telemetry)
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
    onLoadMore,
    searchInProgress,
    onListingClick,
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  // Single localStorage read for the whole results grid (empty until after
  // mount, then populated) — each card gets an isViewed boolean, not its own read.
  const viewedListings = useViewedListings();

  // 🔴 Story 12.15 (AC-5c) — ONE decoded-size map for the whole surface. Each
  // card reports its own `<img>`'s decode into it on load; nothing is fetched.
  const { decodedSizeFor, onImageDecoded } = useCardDecodedSizes();


  const totalItems = pagination?.totalItems ?? 0;
  const loadedCount = listings.length;
  const hasMore = totalItems > 0 && loadedCount < totalItems;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Matches .listingCards grid: 1 / 3 / 4 / 5 columns
      return [
        '(max-width: 767px) 100vw',
        '(max-width: 1023px) 33vw',
        `(max-width: 1399px) 25vw`,
        `20vw`,
      ].join(', ');
    }
  };

  const viewMoreFooter = pagination ? (
    hasMore ? (
      <div className={css.viewMoreWrap}>
        <button
          className={css.viewMoreBtn}
          onClick={onLoadMore}
          disabled={searchInProgress}
          type="button"
        >
          View More
          <ChevronDown />
          <span className={css.viewMoreCount}>
            {loadedCount} of {totalItems}
          </span>
        </button>
      </div>
    ) : totalItems > 0 ? (
      <div className={css.viewMoreWrap}>
        <p className={css.allShown}>
          You&rsquo;ve seen all {totalItems} {totalItems === 1 ? 'horse' : 'horses'}
        </p>
      </div>
    ) : null
  ) : null;

  return (
    <div className={classes}>
      <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map(l => (
          <li key={l.id.uuid} className={css.resultItem}>
            <ListingCard
              className={css.listingCard}
              listing={l}
              renderSizes={cardRenderSizes(isMapVariant)}
              setActiveListing={setActiveListing}
              onCardClick={onListingClick}
              isViewed={viewedListings.has(l.id.uuid)}
              /* 🔴 Story 12.15 (AC-5c) — the decoded-size map lives HERE, not in
                 each card: 24 hooks in a .map() would be a rules-of-hooks
                 violation the moment the result count changes. */
              decodedSize={decodedSizeFor(l)}
              onImageDecoded={onImageDecoded}
              sourcePage="search"
            />
          </li>
        ))}
        {props.children}
      </ul>
      {viewMoreFooter}
    </div>
  );
};

export default SearchResultsPanel;
